import type { WallboxPhaseType } from 'src/Libs/wallboxes/types'
import React, { useCallback, useEffect, useState } from 'react'
import { FormikTabButtonGroup } from 'src/Libs/inputs/formik'
import type { ButtonAction } from 'src/Libs/inputs/formik'
import { useField, useFormikContext } from 'formik'
import { WallboxConfigurationModel } from 'src/Libs/wallboxes'
import { WallboxType } from 'src/Libs/wallboxes/api'
import { Typography, useMediaQuery, useTheme } from '@mui/material'
import { capitalize } from 'lodash'

export interface Props {
  numberPhasesFieldName: string
  autoPhaseSwitchEnabledFieldName: string
  model: WallboxConfigurationModel
  disabled?: boolean
  phaseConfig: {
    phases: Map<WallboxType, WallboxPhaseType[]>
    wallboxTypesWithAutoMode: WallboxType[]
    autoSwitchValue: string
  }
}

const PhaseToggleField: React.FC<Props> = ({ phaseConfig, numberPhasesFieldName, autoPhaseSwitchEnabledFieldName, model, disabled = false }) => {
  const { setFieldValue } = useFormikContext()
  const { breakpoints } = useTheme()
  const fullWidthField = useMediaQuery(breakpoints.down('md'))

  const readonly = [WallboxType.Bidi].includes(model.wallboxType)
  const phasesByType = phaseConfig.phases.get(model.wallboxType) || []
  const doesSupportAutoMode = phaseConfig.wallboxTypesWithAutoMode.includes(model.wallboxType)
  const [currentActive, setCurrentActive] = useState<number>()

  const actions: ButtonAction[] = phasesByType.map(value => ({ label: value.toString(), id: value.toString() })) as ButtonAction[]

  if (doesSupportAutoMode) {
    actions.push({ label: capitalize(phaseConfig.autoSwitchValue), id: phaseConfig.autoSwitchValue })
  }

  const [numberPhasesField] = useField<number>(numberPhasesFieldName)
  const [autoPhaseSwitchEnabledField] = useField<number>(autoPhaseSwitchEnabledFieldName)
  const initialActiveIndex = actions.findIndex(item => {
    if (autoPhaseSwitchEnabledField.value && item.id === phaseConfig.autoSwitchValue) {
      return true
    }
    return !autoPhaseSwitchEnabledField.value && item.id === numberPhasesField.value.toString()
  }, 0)

  useEffect(() => {
    setCurrentActive(initialActiveIndex)
  }, [initialActiveIndex])

  const handleChange = useCallback(
    (selectedGroupIndex: number) => {
      setCurrentActive(selectedGroupIndex)
      const currentAction = actions.find((_, index) => index === selectedGroupIndex, null) || null

      if (!currentAction) {
        console.warn('Unsupported index', selectedGroupIndex)
        return
      }

      if (currentAction.id === phaseConfig.autoSwitchValue) {
        setFieldValue(autoPhaseSwitchEnabledFieldName, true)
      } else {
        setFieldValue(numberPhasesFieldName, currentAction.id)
        setFieldValue(autoPhaseSwitchEnabledFieldName, false)
      }
    },
    [actions, autoPhaseSwitchEnabledFieldName, numberPhasesFieldName, phaseConfig.autoSwitchValue, setFieldValue]
  )

  if (phasesByType) {
    return (
      <FormikTabButtonGroup
        active={currentActive}
        actions={actions}
        onChange={handleChange}
        fullWidth={fullWidthField}
        disabled={readonly || disabled}
      />
    )
  } else {
    return <Typography variant={'body1'}>{'systemSettings.wallbox.errors.unsupportedType'}</Typography>
  }
}

export default PhaseToggleField
